import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormattedNumberInput } from "@/components/ui/formatted-number-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient, getCsrfHeaders } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { useVerificationStatus } from "@/components/VerificationBanner";
import { Mail } from "lucide-react";
import { EmailVerificationModal } from "@/components/EmailVerificationModal";
import { UpgradeModal } from "@/components/UpgradeModal";
import { EQUIPMENT_TYPES } from "@/lib/equipmentTypes";

export default function PostTruck() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { canPost, isLoading: verificationLoading } = useVerificationStatus();
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  useEffect(() => {
    if (user && user.emailVerified === false) {
      setShowEmailVerificationModal(true);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (user.membershipTier === "Free") {
      setUpgradeModalOpen(true);
    }
  }, [user]);
  
  const [formData, setFormData] = useState({
    originCity: "",
    originState: "",
    destinationCity: "",
    destinationState: "",
    destinationFlexible: false,
    availableFrom: "",
    availableTo: "",
    description: "",
    equipmentType: "",
    availablePallets: "",
    availableWeight: "",
    availableLinearFeet: "",
    acceptsStackable: false,
    acceptsHazardous: false,
    acceptsPartialLane: false,
  });

  const [destinationError, setDestinationError] = useState("");

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch("/api/trucks", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          throw new Error("Failed to post truck space");
        }
        throw new Error(errorData.message || errorData.error || "Failed to post truck space");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Truck space posted successfully!",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/trucks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      setLocation("/dashboard");
    },
    onError: (error: any) => {
      const errorMessage = error.message || "";
      if (errorMessage.includes("CARRIER_UPGRADE_REQUIRED")) {
        setUpgradeModalOpen(true);
      } else {
        toast({
          title: "Error posting truck space",
          description: errorMessage,
          variant: "destructive",
        });
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.equipmentType) {
      toast({ title: "Equipment type required", description: "Please select an equipment type.", variant: "destructive" });
      return;
    }

    const destinationValid = formData.destinationFlexible || (formData.destinationCity.trim() && formData.destinationState.trim());
    if (!destinationValid) {
      setDestinationError("Please enter a destination city and state, or select Flexible");
      return;
    }
    setDestinationError("");

    const origin = `${formData.originCity}, ${formData.originState}`;
    let destination: string;
    if (formData.destinationCity.trim() && formData.destinationState.trim()) {
      destination = `${formData.destinationCity}, ${formData.destinationState}`;
    } else if (formData.destinationFlexible) {
      destination = "Flexible";
    } else {
      destination = `${formData.destinationCity}, ${formData.destinationState}`;
    }
    
    mutation.mutate({
      userId: "temp-user-id",
      origin,
      originCity: formData.originCity,
      originState: formData.originState,
      destination,
      destinationCity: formData.destinationCity,
      destinationState: formData.destinationState,
      destinationFlexible: formData.destinationFlexible,
      capacity: formData.availableWeight ? parseInt(formData.availableWeight) : 0,
      dimensions: "Standard",
      availableFrom: new Date(formData.availableFrom).toISOString(),
      availableTo: formData.availableTo ? new Date(formData.availableTo).toISOString() : new Date(formData.availableFrom).toISOString(),
      description: formData.description,
      status: "Available",
      equipmentType: formData.equipmentType || null,
      availablePallets: formData.availablePallets ? parseInt(formData.availablePallets) : null,
      availableWeight: formData.availableWeight ? parseInt(formData.availableWeight) : null,
      availableLinearFeet: formData.availableLinearFeet ? parseInt(formData.availableLinearFeet) : null,
      acceptsStackable: formData.acceptsStackable,
      acceptsHazardous: formData.acceptsHazardous,
      acceptsPartialLane: formData.acceptsPartialLane,
    });
  };

  if (verificationLoading) return null;

  if (user && user.emailVerified === false) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <EmailVerificationModal
          isOpen={showEmailVerificationModal}
          onClose={() => setShowEmailVerificationModal(false)}
        />
        <h1 className="text-3xl font-bold mb-6" data-testid="text-post-truck-title">Post Available Truck Space</h1>
        <Card className="p-12 text-center">
          <Mail className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
          <p className="font-medium text-muted-foreground" data-testid="text-email-verification-required">Email verification required</p>
          <p className="text-sm text-muted-foreground mt-1">Verify your email address to post available truck space.</p>
          <Button
            className="mt-4"
            onClick={() => setShowEmailVerificationModal(true)}
            data-testid="button-verify-email-prompt"
          >
            Verify Email
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Post Available Truck Space</h1>
      
      <Card className="p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Origin</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="originCity">City *</Label>
                <Input 
                  id="originCity"
                  value={formData.originCity}
                  onChange={(e) => setFormData({...formData, originCity: e.target.value})}
                  data-testid="input-origin-city"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="originState">State *</Label>
                <Input 
                  id="originState"
                  value={formData.originState}
                  onChange={(e) => setFormData({...formData, originState: e.target.value})}
                  data-testid="input-origin-state"
                  required
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Destination</h3>
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 transition-opacity${formData.destinationFlexible ? " opacity-50" : ""}`}>
              <div className="space-y-2">
                <Label htmlFor="destinationCity">City</Label>
                <Input 
                  id="destinationCity"
                  value={formData.destinationCity}
                  onChange={(e) => { setFormData({...formData, destinationCity: e.target.value}); setDestinationError(""); }}
                  data-testid="input-destination-city"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="destinationState">State</Label>
                <Input 
                  id="destinationState"
                  value={formData.destinationState}
                  onChange={(e) => { setFormData({...formData, destinationState: e.target.value}); setDestinationError(""); }}
                  data-testid="input-destination-state"
                />
              </div>
            </div>

            <div className="mt-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="destinationFlexible"
                  checked={formData.destinationFlexible}
                  onCheckedChange={(checked) => { setFormData({...formData, destinationFlexible: checked === true}); setDestinationError(""); }}
                  data-testid="checkbox-destination-flexible"
                />
                <Label htmlFor="destinationFlexible" className="cursor-pointer font-medium text-sm">Flexible — open to any destination</Label>
              </div>
              <p className="text-sm text-muted-foreground mt-1 ml-6">Check this if you're open to hauling freight in any direction</p>
            </div>

            {destinationError && (
              <p className="text-sm text-destructive" data-testid="text-destination-error">{destinationError}</p>
            )}

            <div className="mt-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="acceptsPartialLane"
                  checked={formData.acceptsPartialLane}
                  onCheckedChange={(checked) => setFormData({...formData, acceptsPartialLane: checked === true})}
                  data-testid="checkbox-accepts-partial-lane"
                />
                <Label htmlFor="acceptsPartialLane" className="cursor-pointer font-medium">Accepts Partial Lane Matches</Label>
              </div>
              <p className="text-sm text-muted-foreground mt-1 ml-6">Include this truck in searches and matching that share either your origin or destination, not just both</p>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Equipment & Capacity</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="equipmentType">Equipment Type *</Label>
                <Select
                  value={formData.equipmentType}
                  onValueChange={(value) => setFormData({...formData, equipmentType: value})}
                >
                  <SelectTrigger data-testid="select-equipment-type">
                    <SelectValue placeholder="Select equipment type" />
                  </SelectTrigger>
                  <SelectContent>
                    {EQUIPMENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="availableWeight">Available Weight (lbs) *</Label>
                <FormattedNumberInput 
                  id="availableWeight"
                  decimals={0}
                  value={formData.availableWeight}
                  onChange={(val) => setFormData({...formData, availableWeight: val})}
                  data-testid="input-available-weight"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="availablePallets">Available Pallet Positions</Label>
                <FormattedNumberInput 
                  id="availablePallets"
                  decimals={0}
                  value={formData.availablePallets}
                  onChange={(val) => setFormData({...formData, availablePallets: val})}
                  data-testid="input-available-pallets"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="availableLinearFeet">Available Linear Feet</Label>
                <FormattedNumberInput 
                  id="availableLinearFeet"
                  decimals={0}
                  value={formData.availableLinearFeet}
                  onChange={(val) => setFormData({...formData, availableLinearFeet: val})}
                  data-testid="input-available-linear-feet"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6 pt-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="acceptsStackable"
                  checked={formData.acceptsStackable}
                  onCheckedChange={(checked) => setFormData({...formData, acceptsStackable: checked === true})}
                  data-testid="checkbox-accepts-stackable"
                />
                <Label htmlFor="acceptsStackable" className="cursor-pointer">Accepts stackable freight</Label>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="acceptsHazardous"
                  checked={formData.acceptsHazardous}
                  onCheckedChange={(checked) => setFormData({...formData, acceptsHazardous: checked === true})}
                  data-testid="checkbox-accepts-hazardous"
                />
                <Label htmlFor="acceptsHazardous" className="cursor-pointer">Accepts hazardous materials</Label>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="availableFrom">Available From *</Label>
              <Input 
                id="availableFrom"
                type="date"
                value={formData.availableFrom}
                onChange={(e) => setFormData({...formData, availableFrom: e.target.value})}
                data-testid="input-available-from"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="availableTo">Available To</Label>
              <Input 
                id="availableTo"
                type="date"
                value={formData.availableTo}
                onChange={(e) => setFormData({...formData, availableTo: e.target.value})}
                data-testid="input-available-to"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Additional Information</Label>
            <Textarea 
              id="description"
              placeholder="Any special equipment, certifications, or route details..."
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              data-testid="textarea-description"
              rows={4}
            />
          </div>

          <div className="flex gap-4">
            <Button 
              type="submit" 
              size="lg" 
              className="flex-1" 
              data-testid="button-post-truck"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Posting..." : "Post Truck Space"}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              size="lg"
              onClick={() => setLocation("/dashboard")}
              data-testid="button-cancel"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>

      <UpgradeModal
        open={upgradeModalOpen}
        onClose={() => {
          setUpgradeModalOpen(false);
          setLocation("/dashboard");
        }}
        reason="CARRIER_UPGRADE_REQUIRED"
      />
    </div>
  );
}
